// Cross-condition overlap check — names takeoff that is probably COUNTED TWICE, so a person decides.
//
// The same HEPA / door / wall run is taken off across many drawings on purpose, and different trades'
// conditions legitimately sit on the same floor (floor finish + ceiling + wall protection). What is almost
// never intended is the same thing measured twice on one drawing, so only these cases are reported:
//   overlap_area      two area runs of the SAME type overlap (certain double count), or of DIFFERENT types
//                     under the same cost code (two floor systems on one floor) — overlapping SF
//   overlap_run       two linear runs of the same type (or same cost code) lie along each other — overlapping LF
//   duplicate_symbol  two placed symbols of the same type sit on the same spot
// Nothing is changed or removed; findings feed the Review queue (Geometry) and click through to the rows.
//
// Scope and exclusions (each is a way to be wrong if it were otherwise):
//   - per drawing: coordinates only mean something on their own sheet; a row with no sheet is its own group
//   - back-outs (MeasureRole 'deduct') are not takeoff
//   - two auto-generated rows are skipped: the engines that wrote them already own their placement and dedupe
//   - a drawn row never overlaps itself
// Pure: no I/O. Tolerances come from Takeoff Variables (db/65) through the optional `tunables` argument.

import { isBackOut, ringOfGeometry, clippedDeductionAreas } from "./takeoffExclusions";
import { DEFAULT_TUNABLES } from "./takeoffGeometry";

export const OVERLAP_CODES = Object.freeze({
  overlap_area:     { label: "Area runs overlap" },
  overlap_run:      { label: "Linear runs overlap" },
  duplicate_symbol: { label: "Duplicate symbol" },
});

const round1 = (v) => Math.round(v * 10) / 10;
const geometryOf = (row) => { try { const g = row?.GeometryJson; return (typeof g === "string" ? JSON.parse(g || "{}") : g) || {}; } catch { return {}; } };
const isGenerated = (c) => !!(c?.AutoKey || c?.AutoWallKey);

/** The catalog row a drawn row is an instance of — same type means same entity key. */
export function entityKeyOf(c) {
  if (c?.Kind === "item" && c.ItemID != null) return `item:${c.ItemID}`;
  if (c?.Kind === "assembly" && c.AssemblyID != null) return `assembly:${c.AssemblyID}`;
  if (c?.TakeoffConditionTypeID != null) return `type:${c.TakeoffConditionTypeID}`;
  if (c?.TakeoffConditionLibraryID != null) return `library:${c.TakeoffConditionLibraryID}`;
  return null;
}

const nameOf = (c) => c?.Name || c?.Code || "A condition";

// How two rows relate: same type, or different types under one cost code, else unrelated (legitimately co-located).
function relation(a, b) {
  const ka = entityKeyOf(a), kb = entityKeyOf(b);
  if (ka && ka === kb) return "same";
  if (a.CostCodeID != null && a.CostCodeID === b.CostCodeID) return "cost_code";
  return null;
}

function pointsOf(row) {
  const g = geometryOf(row);
  return Array.isArray(g.points) ? g.points.filter(p => Number.isFinite(+p?.x) && Number.isFinite(+p?.y)).map(p => ({ x: +p.x, y: +p.y })) : [];
}

function bbox(pts) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of pts) { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y); }
  return { x0, y0, x1, y1 };
}
const boxesTouch = (a, b, pad = 0) => a.x0 <= b.x1 + pad && b.x0 <= a.x1 + pad && a.y0 <= b.y1 + pad && b.y0 <= a.y1 + pad;

// Length two segments run ALONG each other: parallel within the angle tolerance, closer than the line tolerance,
// counting only the stretch they share.
function sharedRunLength(a1, a2, b1, b2, lineTolFt, angleTolDeg) {
  const ax = a2.x - a1.x, ay = a2.y - a1.y, bx = b2.x - b1.x, by = b2.y - b1.y;
  const la = Math.hypot(ax, ay), lb = Math.hypot(bx, by);
  if (la < 1e-9 || lb < 1e-9) return 0;
  const sinAngle = Math.abs(ax * by - ay * bx) / (la * lb);
  if (sinAngle > Math.sin((angleTolDeg * Math.PI) / 180)) return 0;
  const ux = ax / la, uy = ay / la;
  const dist = (p) => Math.abs((p.x - a1.x) * uy - (p.y - a1.y) * ux);
  if (dist(b1) > lineTolFt || dist(b2) > lineTolFt) return 0;
  const t1 = (b1.x - a1.x) * ux + (b1.y - a1.y) * uy, t2 = (b2.x - a1.x) * ux + (b2.y - a1.y) * uy;
  return Math.max(0, Math.min(la, Math.max(t1, t2)) - Math.max(0, Math.min(t1, t2)));
}

function sharedRunBetween(pa, pb, lineTolFt, angleTolDeg) {
  let total = 0;
  for (let i = 0; i + 1 < pa.length; i++) for (let j = 0; j + 1 < pb.length; j++) total += sharedRunLength(pa[i], pa[i + 1], pb[j], pb[j + 1], lineTolFt, angleTolDeg);
  return total;
}

const lengthOf = (pts) => { let s = 0; for (let i = 0; i + 1 < pts.length; i++) s += Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y); return s; };

/**
 * Findings for a set of drawn rows. Returns
 *   [{ code, severity, sheetId, roomId, conditionIds: [a, b], amount, unit, relation, message }]
 * `rooms` is optional (only to name the room in a message).
 */
export function findOverlaps(conditions, { rooms = [], tunables = DEFAULT_TUNABLES } = {}) {
  const t = { ...DEFAULT_TUNABLES, ...(tunables || {}) };
  const minAreaSf = t.OVERLAP_MIN_AREA_SF, minRunFt = t.OVERLAP_MIN_RUN_FT, lineTolFt = t.OVERLAP_LINE_TOL_FT, dupFt = t.DUPLICATE_SYMBOL_FT;
  const roomName = (id) => rooms.find(r => r.RoomID === id)?.RoomName;

  // Group the rows that can be checked, by drawing.
  const groups = new Map();
  for (const c of conditions || []) {
    if (!c || isBackOut(c) || !c.Style) continue;
    const key = c.SheetID ?? "none";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  }

  const out = [];
  const push = (a, b, f) => out.push({
    severity: "warn", sheetId: a.SheetID ?? null, roomId: a.RoomID ?? b.RoomID ?? null,
    conditionIds: [a.TakeoffConditionDrawnID, b.TakeoffConditionDrawnID], ...f,
  });
  const where = (a, b) => { const n = roomName(a.RoomID ?? b.RoomID); return n ? ` in ${n}` : ""; };

  for (const rows of groups.values()) {
    const shaped = rows.map(c => {
      if (c.Style === "area") { const ring = ringOfGeometry(c.GeometryJson); return ring ? { c, kind: "area", ring, box: bbox(ring) } : null; }
      if (c.Style === "linear") { const pts = pointsOf(c); return pts.length >= 2 ? { c, kind: "linear", pts, box: bbox(pts) } : null; }
      if (c.Style === "count") { const g = geometryOf(c); return Number.isFinite(+g.x) && Number.isFinite(+g.y) ? { c, kind: "count", at: { x: +g.x, y: +g.y } } : null; }
      return null;
    }).filter(Boolean);

    for (let i = 0; i < shaped.length; i++) {
      for (let j = i + 1; j < shaped.length; j++) {
        const A = shaped[i], B = shaped[j];
        if (A.kind !== B.kind) continue;
        if (isGenerated(A.c) && isGenerated(B.c)) continue;
        const rel = relation(A.c, B.c);
        if (!rel) continue;

        if (A.kind === "area") {
          if (!boxesTouch(A.box, B.box)) continue;
          const sf = clippedDeductionAreas(A.ring, [B.ring]).eachInside[0];
          if (!(sf >= minAreaSf)) continue;
          const names = nameOf(A.c) === nameOf(B.c) ? `Two ${nameOf(A.c)} areas` : `${nameOf(A.c)} and ${nameOf(B.c)}`;
          push(A.c, B.c, { code: "overlap_area", amount: sf, unit: "SF", relation: rel,
            message: rel === "same"
              ? `${names} overlap by ${round1(sf)} SF${where(A.c, B.c)} — that area is counted twice.`
              : `${names} overlap by ${round1(sf)} SF${where(A.c, B.c)} — two types under the same cost code cover the same ground.` });
        } else if (A.kind === "linear") {
          if (!boxesTouch(A.box, B.box, lineTolFt)) continue;
          const lf = Math.min(sharedRunBetween(A.pts, B.pts, lineTolFt, t.PARALLEL_ANGLE_TOL_DEG), lengthOf(A.pts), lengthOf(B.pts));
          if (!(lf >= minRunFt)) continue;
          const names = nameOf(A.c) === nameOf(B.c) ? `Two ${nameOf(A.c)} runs` : `${nameOf(A.c)} and ${nameOf(B.c)}`;
          push(A.c, B.c, { code: "overlap_run", amount: lf, unit: "LF", relation: rel,
            message: rel === "same"
              ? `${names} run along each other for ${round1(lf)} LF${where(A.c, B.c)} — that length is counted twice.`
              : `${names} run along each other for ${round1(lf)} LF${where(A.c, B.c)} — two types under the same cost code on the same line.` });
        } else if (rel === "same" && Math.hypot(A.at.x - B.at.x, A.at.y - B.at.y) <= dupFt) {
          push(A.c, B.c, { code: "duplicate_symbol", amount: 1, unit: "ea", relation: rel,
            message: `Two ${nameOf(A.c)} symbols sit on the same spot${where(A.c, B.c)} — likely placed twice.` });
        }
      }
    }
  }
  return out;
}
